import { Component, effect, inject, input } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { EOverwriteStatus, EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { v4 as uuidv4 } from 'uuid';
import { ExecutionExpectedKpisService } from '../execution-expected-kpis.service';
import { ExpectedKpiComponent } from './expected-kpi/expected-kpi.component';
import { IExpectedKpiForm } from './expected-kpi/expected-kpi.interface';
import { ExpectedKpiService } from './expected-kpi/expected-kpi.service';

@Component({
  imports: [ReactiveFormsModule, TranslateModule, ExpectedKpiComponent, NgxSkeletonLoaderModule],
  selector: 'app-expected-kpi-list',
  templateUrl: './expected-kpi-list.component.html',
})
export class ExpectedKpiListComponent {
  public readonly form = input.required<FormArray<FormGroup<IExpectedKpiForm>> | null>();
  public readonly executionId = input.required<string | undefined>();
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly availableKpis = input.required<TExpectedKpi[] | null>();
  public readonly creatorId = input.required<string | null | undefined>();
  public readonly ctaTemplateId = input<string | undefined>(undefined);
  public readonly isCustomMode = input.required<boolean>();
  public readonly creators = input.required<ICreator[]>();
  public readonly isParentDeleteDisabled = input.required<boolean>();

  public readonly isViewMode = input<boolean>(false);

  private readonly translate = inject(TranslateService);

  private isReconciling = false;

  constructor() {
    effect(() => {
      this.syncExpectedKpisWithAvailableKpis();
    });

    effect((onCleanup) => {
      const form = this.form();

      if (!form) {
        return;
      }

      // The sync effect only re-runs on context switches - M1/M2 also need to react to value
      // changes (a shared value typed or erased in global mode).
      const reconcileSubscription = form.valueChanges.subscribe(() => {
        this.reconcileKpiRepresentations(form, new Set(this.availableKpis() ?? []));
      });

      const languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
        this.sortExpectedKpisByTranslatedLabel(form);
      });

      onCleanup(() => {
        reconcileSubscription.unsubscribe();
        languageChangeSubscription.unsubscribe();
      });
    });
  }

  // Every context must see the KPI type: global mode shows one shared row (a real value or an
  // empty placeholder); custom mode shows the creator's own exclusive row, or the shared row when
  // it holds a real value (per-creator overrides ride on it as customCtaValues records).
  public isKpiRowVisible(control: FormGroup<IExpectedKpiForm>): boolean {
    const { exclusiveTo, kpi } = control.value;

    if (exclusiveTo) {
      return this.isViewMode() || (this.isCustomMode() && exclusiveTo === this.creatorId());
    }

    if (!kpi || !this.isCustomMode()) {
      return true;
    }

    // The control's expectedValue reflects the per-creator display state (empty when the current
    // creator deleted it) - visibility follows the real global value instead.
    if (this.hasGlobalKpiValue(control)) {
      return true;
    }

    // An empty shared placeholder is hidden for a creator who owns an exclusive row of the type -
    // their row is the editing surface, the placeholder would duplicate it.
    const hasCreatorOwnedRow = this.form()?.controls.some(
      (row) => row.value.kpi === kpi && row.value.exclusiveTo === this.creatorId(),
    );

    return !hasCreatorOwnedRow;
  }

  // The real global value of a shared row lives in its globalCtaValues record - the control's
  // expectedValue is the per-creator display state (an override, or empty for a deleted-marker
  // state) and must not be used to decide global-level semantics.
  private hasGlobalKpiValue(control: FormGroup<IExpectedKpiForm>): boolean {
    const globalValue = control.value.globalCtaValues?.find((value) => value.valueName === 'expected_value');

    return ExecutionExpectedKpisService.isExpectedValueSet(globalValue?.value);
  }

  private syncExpectedKpisWithAvailableKpis(): void {
    const form = this.form();
    const availableKpis = this.availableKpis();

    if (!form) {
      return;
    }

    if (!availableKpis?.length) {
      return;
    }

    const availableKpiSet = new Set(availableKpis);
    const creatorId = this.creatorId();

    this.reconcileKpiRepresentations(form, availableKpiSet);

    availableKpis.forEach((kpiKey) => {
      const existingRow = form.controls.find((control) => {
        const { exclusiveTo, kpi } = control.value;

        if (kpi !== kpiKey) {
          return false;
        }

        if (!creatorId) {
          // Global mode: a shared row (a real value or an empty placeholder) covers the type.
          return !exclusiveTo;
        }

        // Custom mode: the creator's own exclusive row, or a shared row holding a real global
        // value (per-creator overrides ride on it as customCtaValues records).
        if (exclusiveTo) {
          return exclusiveTo === creatorId;
        }

        return this.hasGlobalKpiValue(control);
      });

      if (existingRow) {
        return;
      }

      const expectedKpiForm = ExpectedKpiService.getConfig().createForm({
        exclusiveTo: creatorId ?? null,
        executionId: this.executionId() ?? '',
        expectedValue: null,
        id: uuidv4(),
        kpi: kpiKey,
      });

      form.push(expectedKpiForm);
    });

    this.sortExpectedKpisByTranslatedLabel(form);
  }

  // Keeps the per-type representation consistent with the backend's contract: a shared row may
  // only be submitted with a real expected_value (the backend rejects null), so per-creator
  // values live in exclusiveTo rows while the type has no shared value, and move into
  // customCtaValues records on the shared row once a real global value exists. Runs on every
  // change and context switch; both directions are lossless and idempotent (the flag guards
  // against re-entry from this method's own form mutations).
  private reconcileKpiRepresentations(
    form: FormArray<FormGroup<IExpectedKpiForm>>,
    availableKpiSet: Set<TExpectedKpi>,
  ): void {
    if (this.isReconciling) {
      return;
    }

    this.isReconciling = true;

    try {
      availableKpiSet.forEach((kpiKey) => {
        const rowsOfType = form.controls.filter((control) => control.value.kpi === kpiKey);
        const globalRow = rowsOfType.find((control) => !control.value.exclusiveTo);

        if (!globalRow) {
          return;
        }

        // Decided by the real global value (globalCtaValues), not the control: the control is the
        // per-creator display state, so a creator's deletion (empty control + marker) must never
        // look like the global value was erased.
        if (this.hasGlobalKpiValue(globalRow)) {
          this.migrateExclusiveValuesToGlobalRow(form, globalRow, rowsOfType);
        } else {
          this.migrateGlobalRecordsToExclusiveRows(form, globalRow, kpiKey);
        }
      });
    } finally {
      this.isReconciling = false;
    }
  }

  // M1 - a real shared value exists: every exclusiveTo value moves onto the shared row as a
  // customCtaValues record (per-creator overrides must ride on a real shared value), and the
  // exclusive rows are removed.
  private migrateExclusiveValuesToGlobalRow(
    form: FormArray<FormGroup<IExpectedKpiForm>>,
    globalRow: FormGroup<IExpectedKpiForm>,
    rowsOfType: FormGroup<IExpectedKpiForm>[],
  ): void {
    rowsOfType
      .filter((control) => control.value.exclusiveTo)
      .forEach((exclusiveToControl) => {
        const { exclusiveTo, expectedValue } = exclusiveToControl.value;

        if (exclusiveTo && expectedValue !== null && expectedValue !== undefined) {
          const customCtaValues: ICustomCtaValue[] =
            (globalRow.get('customCtaValues')?.value as ICustomCtaValue[] | null) ?? [];

          globalRow.get('customCtaValues')?.setValue([
            ...customCtaValues,
            {
              creatorId: exclusiveTo,
              id: uuidv4(),
              status: EOverwriteStatus.overwritten,
              value: String(expectedValue),
              valueName: 'expected_value',
            },
          ]);
        }

        const removeIndex = form.controls.indexOf(exclusiveToControl);

        if (removeIndex !== -1) {
          form.removeAt(removeIndex);
        }
      });
  }

  // M2 - the shared value was erased ("counts as deleted": the row will be dropped from the
  // payload), so its per-creator records must become exclusiveTo rows first or they would be lost
  // with it. Deleted markers are dropped - without a shared value they are moot.
  private migrateGlobalRecordsToExclusiveRows(
    form: FormArray<FormGroup<IExpectedKpiForm>>,
    globalRow: FormGroup<IExpectedKpiForm>,
    kpiKey: TExpectedKpi,
  ): void {
    const customCtaValues: ICustomCtaValue[] =
      (globalRow.get('customCtaValues')?.value as ICustomCtaValue[] | null) ?? [];
    const valueRecords = customCtaValues.filter(
      (record) =>
        record.valueName === 'expected_value' &&
        !!record.creatorId &&
        ExecutionExpectedKpisService.isExpectedValueSet(record.value),
    );

    if (!valueRecords.length) {
      return;
    }

    globalRow.get('customCtaValues')?.setValue([], { emitEvent: false });

    valueRecords.forEach((record) => {
      form.push(
        ExpectedKpiService.getConfig().createForm({
          exclusiveTo: record.creatorId!,
          executionId: this.executionId() ?? '',
          expectedValue: Number(record.value),
          id: uuidv4(),
          kpi: kpiKey,
        }),
      );
    });
  }

  private getExpectedKpiTranslatedLabel(kpi: TExpectedKpi | null): string {
    if (!kpi) {
      return '';
    }

    return this.translate.instant(`shared.expectedKpis.${kpi}`);
  }

  private sortExpectedKpisByTranslatedLabel(form: FormArray<FormGroup<IExpectedKpiForm>>): void {
    if (form.length < 2) {
      return;
    }

    form.controls.sort((firstControl, secondControl) => {
      const firstKpi: TExpectedKpi | null = firstControl.controls.kpi.value;
      const secondKpi: TExpectedKpi | null = secondControl.controls.kpi.value;
      const firstLabel: string = this.getExpectedKpiTranslatedLabel(firstKpi);
      const secondLabel: string = this.getExpectedKpiTranslatedLabel(secondKpi);
      const labelComparison: number = firstLabel.localeCompare(secondLabel, this.translate.currentLang || undefined);

      if (labelComparison !== 0) {
        return labelComparison;
      }

      return (firstKpi ?? '').localeCompare(secondKpi ?? '');
    });

    form.updateValueAndValidity({ emitEvent: false });
  }
}
